// Phase NOTARY-FOCUS N1 — /notary/for/$nationality (TH)
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { ScrollText, ShieldCheck, MessageCircle, Phone, Globe2, CheckCircle2 } from "lucide-react";
import { NOTARY_NATIONALITIES, getNotaryNationality, type NotaryNationality } from "@/data/notary-nationalities";
import { getNotaryAct, type NotaryAct } from "@/data/notary-acts";
import { hreflangLinks, SEO_SITE, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import RelatedNotaryLinks from "@/components/RelatedNotaryLinks";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";


export const Route = createFileRoute("/notary/for/$nationality")({
  head: ({ params }) => {
    const n = getNotaryNationality(params.nationality);
    if (!n) return {};
    const title = `Notary Public สำหรับ${n.nameTh} ในกรุงเทพฯ — รับรองเอกสารใช้ในประเทศ ${n.nameEn === "Russian nationals" ? "รัสเซีย" : n.nameEn}| NYC`.slice(0, 70);
    const desc = `บริการ Notary Public สำหรับ${n.nameTh}ที่ต้องการรับรองเอกสารใช้ในประเทศของตนเอง — POA, Affidavit, Single Status, Certified Copy เสร็จ 1 วันทำการ — ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;
    const url = `${SEO_SITE}/notary/for/${n.slug}`;
    return {
      meta: [
        { title: ctaTitle(title, "/notary/for/$nationality", "th") }, { name: "description", content: ctaDesc(desc, "/notary/for/$nationality", "th")},
        { property: "og:title", content: ctaTitle(title, "/notary/for/$nationality", "th")}, { property: "og:description", content: ctaDesc(desc, "/notary/for/$nationality", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/notary/for/${n.slug}`, `/en/notary/for/${n.slug}`),
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "LegalService",
          name: `NYC Notary — ${n.nameEn}`, url, areaServed: "TH",
          inLanguage: ["th", n.hreflang],
          priceRange: "฿500–฿5,000",
          serviceType: "Notarial Services Attorney",
          audience: { "@type": "PeopleAudience", geographicArea: { "@type": "Country", name: n.nameEn } },
        }) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "FAQPage", inLanguage: "th",
          mainEntity: n.faqs.map((f) => ({
            "@type": "Question", name: f.qTh,
            acceptedAnswer: { "@type": "Answer", text: f.aTh },
          })),
        }) },
      ],
    };
  },
  loader: ({ params }): { nationality: NotaryNationality } => {
    const n = getNotaryNationality(params.nationality);
    if (!n) throw notFound();
    return { nationality: n };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl p-8">
      <h1 className="text-2xl font-extrabold">ไม่พบหน้า</h1>
      <Link to="/notary" className="mt-3 inline-block text-amber-700 underline">กลับสู่ /notary</Link>
    </main>
  ),
  component: NationalityHubTh,
});

function NationalityHubTh() {
  const { nationality: n } = Route.useLoaderData() as any;
  const acts: (NotaryAct | undefined)[] = n.topActs.map((s: string) => getNotaryAct(s));

  return (
    <main className="min-h-screen bg-white">
      <section className="relative bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-4 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/notary" className="hover:text-gold">Notary Public</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/90">{n.nameTh}</span>
          </nav>

          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ScrollText className="h-3.5 w-3.5" /> Notary Public · {n.flag} {n.nameTh}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            Notary Public สำหรับ<span className="text-gold">{n.nameTh}</span><br className="hidden sm:block" />
            ในกรุงเทพฯ
          </h1>
          {n.localTagline && (
            <p className="mt-3 text-base text-gold/90 sm:text-lg" lang={n.hreflang}>{n.localTagline}</p>
          )}
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            บริการรับรองเอกสารโดย Notarial Services Attorney ขึ้นทะเบียนสภาทนายความแห่งประเทศไทย
            สำหรับ{n.nameTh}ที่อาศัย/ทำธุรกิจในไทยและต้องการเอกสารใช้ในประเทศของตน
          </p>

          <div className="mt-6 rounded-2xl border border-white/20 bg-white/5 p-4">
            <p className="text-[11px] font-bold uppercase tracking-widest text-gold">Legalization Path</p>
            <p className="mt-2 text-sm text-white/90">{n.legalizationTh}</p>
          </div>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <MessageCircle className="h-4 w-4" /> ปรึกษา LINE @nycli
            </a>
            <a href="tel:0832494999"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
            <Link to="/en/notary/for/$nationality" params={{ nationality: n.slug }}
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-5 py-2.5 text-sm font-bold text-white hover:bg-white/10">
              <Globe2 className="h-4 w-4" /> English
            </Link>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
          ทำไม{n.nameTh}ต้องใช้บริการ Notary Public ในไทย
        </h2>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {n.topUseCasesTh.map((u: string) => (
            <li key={u} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
              <span className="text-sm text-slate-700">{u}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Top notary acts for this nationality */}
      <section className="bg-slate-50 px-4 py-14">
        <div className="mx-auto max-w-6xl">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            บริการ Notary ที่{n.nameTh}ใช้บ่อย
          </h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {acts.map((a) => a && (
              <Link key={a.slug} to="/notary/$act" params={{ act: a.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                <div className="font-display text-base font-extrabold text-navy-deep group-hover:text-amber-600">{a.nameTh}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{a.nameEn}</div>
                <div className="mt-2 flex items-center gap-2 text-[11px]">
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 font-bold text-amber-700">฿{a.priceFromTh.toLocaleString()}</span>
                  <span className="text-slate-500">{a.turnaround}</span>
                  <ShieldCheck className="ml-auto h-4 w-4 text-emerald-500 opacity-0 transition group-hover:opacity-100" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Embassy link */}
      {n.embassySlug && (
        <section className="mx-auto max-w-5xl px-4 py-14 sm:px-6">
          <div className="rounded-2xl border border-slate-200 bg-amber-50/40 p-6">
            <p className="text-[11px] font-bold uppercase tracking-widest text-amber-700">สถานทูต / Embassy</p>
            <h3 className="mt-1 font-display text-xl font-extrabold text-navy-deep">
              ขั้นตอนรับรองที่สถานทูต{n.nameTh === "ชาวอเมริกัน" ? "สหรัฐฯ" : n.nameTh === "ชาวอังกฤษ" ? "UK" : n.nameTh.replace("ชาว", "")} ในกรุงเทพฯ
            </h3>
            <p className="mt-2 text-sm text-slate-700">{n.legalizationTh}</p>
            <Link to="/embassy/$country" params={{ country: n.embassySlug }}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-navy-deep px-4 py-2 text-xs font-bold text-white hover:bg-navy-deep/90">
              ดูข้อมูลสถานทูต {n.flag}
            </Link>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="bg-slate-50 px-4 py-14">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-6 space-y-3">
            {n.faqs.map((f: { qTh: string; aTh: string; qEn: string; aEn: string }, i: number) => (
              <details key={i} className="group rounded-2xl border border-slate-200 bg-white p-5">
                <summary className="cursor-pointer list-none font-display text-base font-extrabold text-navy-deep">
                  <span className="mr-2 text-amber-600">Q.</span>{f.qTh}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-700">{f.aTh}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Other nationalities */}
      <section className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">บริการ Notary สำหรับสัญชาติอื่น</p>
        <ul className="mt-3 flex flex-wrap gap-2">
          {NOTARY_NATIONALITIES.filter((o) => o.slug !== n.slug).map((o) => (
            <li key={o.slug}>
              <Link to="/notary/for/$nationality" params={{ nationality: o.slug }}
                className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-navy-deep hover:border-amber-400 hover:text-amber-700">
                {o.flag} {o.nameTh}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <RelatedNotaryLinks context="nationality" lang="th" currentNationality={n.slug} />
      <QuotableStatsStrip cluster="notary" locale="th" variant="light" />
      <TrustLayer variant="compact" />
    </main>

  );
}
